/**
 * ProjectSchedule.jsx
 * TV-Optimized Production Meeting Dashboard
 *
 * Card Layout (per Mark's sketch):
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ PM  │ Quote # & Internal  │ Product   │ Stage │ Gate Dates        │ Ship   │
 * │     │ Proj Name           │ Types     │       │ Proof/Finals/Ship │ Date   │
 * │Sales│ - Orders & Ship     │ - Opti?   │       │ Submit/Prints     │ &      │
 * │     │ # Jobs              │ - Cutting?│       │                   │Shipping│
 * │     │ [Link to Work Order]│           │       │                   │ Info   │
 * └─────────────────────────────────────────────────────────────────────────────┘
 */

import { useState, useEffect, useCallback, useMemo } from "react";

export default function ProjectSchedule({ onBackToApp, onNavigateToOrder }) {
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [activeView, setActiveView] = useState("inprogress");
  const [stageFilter, setStageFilter] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [focusedProject, setFocusedProject] = useState(null); // Highlighted project for discussion
  const [sortColumn, setSortColumn] = useState("shipDate"); // Default sort by ship date
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'
  const [shipDateFilter, setShipDateFilter] = useState(null); // 'today' or 'thisWeek' or null

  const fetchSchedule = useCallback(async () => {
    const token = sessionStorage.getItem("msal.backendToken");
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/project-schedule", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
      const data = await res.json();
      setScheduleData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    const token = sessionStorage.getItem("msal.backendToken");
    if (!token) return;
    setRefreshing(true);
    await fetch("/api/project-schedule/refresh", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchSchedule();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Date helpers
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const startOfMonth = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // End of this week (Sunday)
  const endOfWeek = useMemo(() => {
    const d = new Date();
    const daysUntilSunday = 7 - d.getDay();
    d.setDate(d.getDate() + daysUntilSunday);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    // Parse as local date (not UTC) to avoid timezone shifting
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  };

  const isThisMonthOrLater = (dateStr) => {
    if (!dateStr) return true;
    // Parse as local date (not UTC) to avoid timezone shifting
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date >= startOfMonth;
  };

  const isPastMonth = (dateStr) => {
    if (!dateStr) return false;
    // Parse as local date (not UTC) to avoid timezone shifting
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date < startOfMonth;
  };

  // Helper to parse ISO date string as local date (avoids timezone shifting)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper to format date string for display
  const formatDateStr = (dateStr) => {
    if (!dateStr) return "-";
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Format date - TV optimized with urgency, shows actual dates and marks late items
  const formatDate = (dateStr, compact = false) => {
    if (!dateStr) return { text: "-", className: "text-gray-600", urgent: false };
    const days = getDaysUntil(dateStr);
    // Parse as local date (not UTC) to avoid timezone shifting
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    if (days === 0) return { text: compact ? "TODAY" : formatted, className: "text-green-400 font-black", urgent: true, isToday: true };
    if (days === 1) return { text: formatted, className: "text-yellow-400 font-bold", urgent: true };
    if (days < 0) return { text: formatted, className: "text-red-500 font-black", urgent: true, late: true };
    if (days <= 3) return { text: formatted, className: "text-orange-400 font-bold", urgent: true };
    if (days <= 7) return { text: formatted, className: "text-yellow-600" };
    return { text: formatted, className: "text-gray-700" };
  };

  const STAGES = {
    1: { label: "Awaiting Art", short: "ART", color: "bg-gray-600", textColor: "text-gray-700" },
    2: { label: "In Queue", short: "QUEUE", color: "bg-purple-600", textColor: "text-purple-600" },
    3: { label: "Proofing", short: "PROOF", color: "bg-blue-600", textColor: "text-blue-600" },
    4: { label: "PA Finals", short: "FINALS", color: "bg-cyan-600", textColor: "text-cyan-600" },
    5: { label: "Assembly", short: "ASSEMBLY", color: "bg-teal-600", textColor: "text-teal-600" },
    6: { label: "Complete", short: "DONE", color: "bg-green-600", textColor: "text-green-600" },
  };

  // Compute metrics and filtered projects
  const { metrics, filteredProjects, projectsByStage } = useMemo(() => {
    const projects = scheduleData?.projects || [];

    const cleanupProjects = projects.filter(p => !p.canceled && p.projectStage !== 6 && isPastMonth(p.shipDate));
    const activeProjects = projects.filter(p => !p.canceled && p.projectStage !== 6 && isThisMonthOrLater(p.shipDate));
    const doneProjects = projects.filter(p => p.projectStage === 6);

    const atRiskCount = activeProjects.filter(p => p.atRisk).length;
    const dueTodayCount = activeProjects.filter(p => getDaysUntil(p.shipDate) === 0).length;
    const dueThisWeek = activeProjects.filter(p => {
      const days = getDaysUntil(p.shipDate);
      return days !== null && days >= 0 && days <= 7;
    }).length;
    const pastDueCount = activeProjects.filter(p => getDaysUntil(p.shipDate) < 0).length;

    // Choose base set for stage counts based on active view
    let viewBaseProjects;
    if (activeView === "inprogress") {
      viewBaseProjects = activeProjects;
    } else if (activeView === "cleanup") {
      viewBaseProjects = cleanupProjects;
    } else if (activeView === "done") {
      viewBaseProjects = doneProjects;
    } else {
      viewBaseProjects = projects;
    }

    // Group by stage - only count projects in current view
    const byStage = {};
    for (let i = 1; i <= 6; i++) byStage[i] = [];
    viewBaseProjects.forEach(p => {
      const stage = p.projectStage || 1;
      if (byStage[stage]) byStage[stage].push(p);
    });

    let filtered = [...projects];

    if (filterText.trim()) {
      const search = filterText.toLowerCase();
      filtered = filtered.filter(p =>
        p.projId?.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search) ||
        p.projectManager?.toLowerCase().includes(search) ||
        p.productionArtist?.toLowerCase().includes(search) ||
        p.quoteNumber?.toLowerCase().includes(search) ||
        p.jobBossData?.orders?.some(o => o.orderNumber?.toLowerCase().includes(search))
      );
    }

    if (stageFilter) {
      filtered = filtered.filter(p => p.projectStage === stageFilter);
    }

    if (activeView === "inprogress") {
      filtered = filtered.filter(p => !p.canceled && p.projectStage !== 6 && isThisMonthOrLater(p.shipDate));
    } else if (activeView === "cleanup") {
      filtered = filtered.filter(p => !p.canceled && p.projectStage !== 6 && isPastMonth(p.shipDate));
    } else if (activeView === "done") {
      filtered = filtered.filter(p => p.projectStage === 6);
    }

    // Apply ship date filter (Today / This Week)
    if (shipDateFilter === "today") {
      filtered = filtered.filter(p => getDaysUntil(p.shipDate) === 0);
    } else if (shipDateFilter === "thisWeek") {
      filtered = filtered.filter(p => {
        const days = getDaysUntil(p.shipDate);
        return days !== null && days >= 0 && days <= 7;
      });
    }

    // Sort by selected column
    filtered.sort((a, b) => {
      // Get values for the sort column
      const getDateValue = (p, col) => {
        switch (col) {
          case "proofDue": return p.proofDue || null;
          case "finalPrintsDue": return p.finalPrintsDue || null;
          case "assemblyDue": return p.assemblyDue || null;
          case "shipDate":
          default: return p.shipDate || null;
        }
      };

      const aDate = getDateValue(a, sortColumn);
      const bDate = getDateValue(b, sortColumn);

      // Handle null/empty dates - always push to end regardless of sort direction
      const aIsEmpty = !aDate || aDate === "";
      const bIsEmpty = !bDate || bDate === "";

      if (aIsEmpty && bIsEmpty) return 0;
      if (aIsEmpty) return 1; // a goes to end
      if (bIsEmpty) return -1; // b goes to end

      // Compare dates (ISO format YYYY-MM-DD sorts correctly as strings)
      const comparison = aDate.localeCompare(bDate);
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return {
      metrics: { atRiskCount, dueTodayCount, dueThisWeek, pastDueCount,
                 inProgressCount: activeProjects.length, cleanupCount: cleanupProjects.length,
                 doneCount: doneProjects.length, total: projects.length },
      filteredProjects: filtered,
      projectsByStage: byStage,
    };
  }, [scheduleData, filterText, activeView, stageFilter, shipDateFilter, sortColumn, sortDirection, today, startOfMonth, endOfWeek]);

  // ═══════════════════════════════════════════════════════════════════════════
  // PROJECT CARD COMPONENT - Based on Mark's sketch
  // ═══════════════════════════════════════════════════════════════════════════
  const ProjectCard = ({ project }) => {
    const stage = STAGES[project.projectStage] || STAGES[1];
    const shipInfo = formatDate(project.shipDate);
    const isExpanded = expandedProject === project.projId;
    const isFocused = focusedProject === project.projId;
    const orderCount = project.jobBossData?.orderCount || 0;
    // Labels show when expanded or focused (hover handled via CSS group-hover)
    const alwaysShowLabels = isExpanded || isFocused;

    // Handle double-click to toggle focus/highlight for discussion
    const handleDoubleClick = (e) => {
      e.stopPropagation();
      setFocusedProject(isFocused ? null : project.projId);
    };

    // Card styling - neutral for all, with focus highlight
    const getCardStyle = () => {
      // Focused card gets prominent highlight
      if (isFocused) {
        return "border-l-8 border-l-cyan-500 bg-cyan-50 ring-2 ring-cyan-300 shadow-lg shadow-cyan-100";
      }
      // All other cards are neutral gray
      return "border-l-4 border-l-gray-300 bg-white";
    };

    // Use CSS group class for hover - more reliable than React state
    const hoverGlowClass = !isFocused
      ? "hover:shadow-[0_0_20px_rgba(168,85,247,0.7),0_0_40px_rgba(251,146,60,0.4),0_0_60px_rgba(168,85,247,0.3)]"
      : "";

    return (
      <div
        className={`group rounded-xl overflow-hidden ${getCardStyle()} ${hoverGlowClass} ${project.onHold ? "opacity-50" : ""} ${isFocused ? "scale-[1.01] z-10 relative" : ""}`}
        onDoubleClick={handleDoubleClick}
      >
        {/* ══════════════ CONDENSED VIEW (Main Card Row) ══════════════ */}
        <div
          className="px-5 py-4 cursor-pointer"
          onClick={() => setExpandedProject(isExpanded ? null : project.projId)}
        >
          <div className="grid grid-cols-12 gap-4 items-center">

            {/* COL 1: PM & PA (1 col) */}
            <div className="col-span-1 text-center border-r border-gray-200 pr-3">
              <div className="text-lg font-bold text-gray-900 truncate" title={project.projectManager}>
                {project.projectManager?.split(' ')[0] || '-'}
              </div>
              <div className="text-xs text-cyan-600 truncate" title={project.productionArtist}>
                {project.productionArtist?.split(' ')[0] || '-'}
              </div>
            </div>

            {/* COL 2: Quote/Project Info (3 cols) */}
            <div className="col-span-3 border-r border-gray-200 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xl font-black text-gray-900">{project.projId}</span>
                {project.onHold && (
                  <span className="px-2 py-0.5 bg-orange-600 text-gray-900 rounded text-xs font-bold">HOLD</span>
                )}
                {orderCount > 0 && (
                  <span className="text-xs text-blue-400 font-medium">
                    {orderCount} order{orderCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="text-base text-gray-700 truncate" title={project.description}>
                {project.description || 'No description'}
              </div>
            </div>

            {/* COL 3: Total Comps (1 col) */}
            <div className="col-span-1 text-center border-r border-gray-200 pr-2">
              <div className="text-[10px] font-medium text-gray-500 uppercase mb-1">Comps</div>
              <div className="text-3xl font-black text-gray-900">{project.totalComps || 0}</div>
              {/* OPTI badge under comps */}
              <div className="flex flex-wrap justify-center gap-1 mt-1">
                {project.opti && project.opti.toLowerCase() !== 'no' && (
                  <span className="px-1.5 py-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-gray-900 rounded text-[10px] font-bold">OPTI</span>
                )}
              </div>
            </div>

            {/* COL 4: POA Count (1 col) */}
            <div className="col-span-1 text-center border-r border-gray-200 pr-2">
              <div className="text-[10px] font-medium text-blue-400/70 uppercase mb-1">POA</div>
              <div className="text-3xl font-black text-blue-400">{project.artworkPOAs || 0}</div>
              {/* Zund badge */}
              <div className="flex flex-wrap justify-center gap-1 mt-1">
                {project.zundWork && (
                  <span className="px-1.5 py-0.5 bg-orange-600/80 text-gray-900 rounded text-[10px] font-bold">Zund</span>
                )}
              </div>
            </div>

            {/* COL 5: Stage (1 col) - just the stage badge, no status */}
            <div className="col-span-1 text-center">
              <div className={`inline-block px-3 py-1.5 rounded-lg ${stage.color}`}>
                <div className="text-sm font-black text-gray-900">{stage.short}</div>
              </div>
            </div>

            {/* COL 6: Gate Dates (3 cols) - highlight next due, orange for future, gray for completed */}
            {(() => {
              // Determine which milestone is "next" (first incomplete one)
              const proofDone = !!project.proofApproved;
              const finalsDone = !!project.finalPrintsComplete;
              const assemblyDone = !!project.assemblyComplete;

              // Next milestone: first one not done
              let nextMilestone = null;
              if (!proofDone) nextMilestone = 'proof';
              else if (!finalsDone) nextMilestone = 'finals';
              else if (!assemblyDone) nextMilestone = 'assembly';

              // Get date styling: green for next (red if late), orange for future pending, gray for done
              const getDateStyle = (milestone, isDone, dateStr) => {
                if (isDone) return 'text-gray-600';
                if (milestone === nextMilestone) {
                  // Check if this date is late (past due)
                  const days = getDaysUntil(dateStr);
                  if (days !== null && days < 0) return 'text-red-500'; // Late!
                  return 'text-green-400'; // Next up, not late
                }
                return 'text-orange-600'; // future pending (consistent light orange)
              };

              return (
                <div className="col-span-3 border-l border-gray-200 pl-4">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {/* Proof Due */}
                    <div className={proofDone ? 'opacity-30' : ''}>
                      <div className={`text-[10px] font-medium text-blue-400/70 uppercase mb-1 ${alwaysShowLabels ? '' : 'hidden group-hover:block'}`}>Proof Due</div>
                      <div className={`text-lg font-black ${getDateStyle('proof', proofDone, project.proofDue)}`}>
                        {formatDate(project.proofDue, true).text}
                      </div>
                      {/* Proof Type - only show if proof NOT approved yet */}
                      {project.proofType && !proofDone && (() => {
                        const proofLower = project.proofType.toLowerCase();
                        const hasOpti = project.opti && project.opti.toLowerCase() !== 'no';
                        const isPhysical = proofLower.includes('physical');
                        const isFlatPrint = proofLower.includes('flat print') || proofLower.includes('flatprint');
                        const needsHighlight = isPhysical || (isFlatPrint && hasOpti);

                        return (
                          <div
                            className={`text-[10px] mt-0.5 truncate ${alwaysShowLabels ? '' : 'hidden group-hover:block'} ${
                              needsHighlight
                                ? 'px-1.5 py-0.5 bg-gradient-to-r from-orange-500 to-pink-500 text-gray-900 rounded font-bold animate-pulse'
                                : 'text-purple-600'
                            }`}
                            title={project.proofType}
                          >
                            {needsHighlight && '⚠️ '}{project.proofType}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Finals Due */}
                    <div className={finalsDone ? 'opacity-30' : ''}>
                      <div className={`text-[10px] font-medium text-cyan-600/70 uppercase mb-1 ${alwaysShowLabels ? '' : 'hidden group-hover:block'}`}>Finals Due</div>
                      <div className={`text-lg font-black ${getDateStyle('finals', finalsDone, project.finalPrintsDue)}`}>
                        {formatDate(project.finalPrintsDue, true).text}
                      </div>
                    </div>
                    {/* Assembly Due */}
                    <div className={assemblyDone ? 'opacity-30' : ''}>
                      <div className={`text-[10px] font-medium text-teal-400/70 uppercase mb-1 ${alwaysShowLabels ? '' : 'hidden group-hover:block'}`}>Assembly Due</div>
                      <div className={`text-lg font-black ${getDateStyle('assembly', assemblyDone, project.assemblyDue)}`}>
                        {formatDate(project.assemblyDue, true).text}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* COL 7: Ship Date (1 col) - label shows on hover/active */}
            <div className="col-span-1 text-center border-l border-gray-200 pl-3">
              <div className={`text-[10px] font-medium text-yellow-400/70 uppercase mb-1 ${alwaysShowLabels ? '' : 'hidden group-hover:block'}`}>Ship Date</div>
              <div className={`text-2xl font-black ${shipInfo.className}`}>
                {shipInfo.text}
              </div>
            </div>

          </div>
        </div>

        {/* ══════════════ EXPANDED VIEW (Click to reveal) ══════════════ */}
        {isExpanded && (
          <div className="border-t border-gray-200 bg-slate-100 px-6 py-5">
            <div className="grid grid-cols-5 gap-6">

              {/* JobBOSS Orders with Product Breakdown */}
              <div>
                <h4 className="text-xs uppercase text-gray-500 tracking-wide mb-3 font-semibold">JobBOSS Orders</h4>
                {project.jobBossData?.orders?.length > 0 ? (
                  <div className="space-y-2">
                    {project.jobBossData.orders.map((order, idx) => (
                      <div key={idx} className="bg-slate-100 rounded-lg px-3 py-2 border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <a
                            href={order.jobBossUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono font-bold text-blue-400 hover:text-blue-600"
                          >
                            {order.orderNumber}
                          </a>
                          <span className={`text-sm ${formatDate(order.dueDate).className}`}>
                            {formatDate(order.dueDate, true).text}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">{order.customerCode}</div>
                        {/* Action buttons row */}
                        <div className="flex gap-2">
                          {/* Work Order link for this order (from user_Memo1) */}
                          {order.workOrderUrl && (
                            <a
                              href={order.workOrderUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-cyan-600/30 hover:bg-cyan-600/50 border border-cyan-500/50 rounded text-cyan-600 hover:text-cyan-100 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              <span className="text-xs font-bold">Work Order</span>
                            </a>
                          )}
                          {/* Track Time button for this specific order */}
                          {onNavigateToOrder && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onNavigateToOrder(project.quoteNumber, order.orderNumber);
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-green-600/30 hover:bg-green-600/50 border border-green-500/50 rounded text-green-600 hover:text-green-100 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-xs font-bold">Track Time</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-600 text-sm">No matching orders in JobBOSS</div>
                )}

                {/* Product Type Breakdown (from Excel tracker) */}
                {(project.shrinks > 0 || project.cartons > 0 || project.labels > 0 || project.bags > 0 || project.other > 0) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-[10px] uppercase text-gray-500 mb-2 font-semibold">Product Breakdown</div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {project.shrinks > 0 && (
                        <span className="px-2 py-1 bg-blue-100 border border-blue-200 rounded text-blue-600">
                          <span className="font-bold">{project.shrinks}</span> Shrink{project.shrinks !== 1 ? 's' : ''}
                        </span>
                      )}
                      {project.cartons > 0 && (
                        <span className="px-2 py-1 bg-green-100 border border-green-200 rounded text-green-600">
                          <span className="font-bold">{project.cartons}</span> Carton{project.cartons !== 1 ? 's' : ''}
                        </span>
                      )}
                      {project.labels > 0 && (
                        <span className="px-2 py-1 bg-yellow-100 border border-yellow-200 rounded text-yellow-600">
                          <span className="font-bold">{project.labels}</span> Label{project.labels !== 1 ? 's' : ''}
                        </span>
                      )}
                      {project.bags > 0 && (
                        <span className="px-2 py-1 bg-purple-100 border border-purple-200 rounded text-purple-600">
                          <span className="font-bold">{project.bags}</span> Bag{project.bags !== 1 ? 's' : ''}
                        </span>
                      )}
                      {project.other > 0 && (
                        <span className="px-2 py-1 bg-slate-100 border border-gray-300/50 rounded text-gray-700">
                          <span className="font-bold">{project.other}</span> Other
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline Details - Due vs Actual */}
              <div>
                <h4 className="text-xs uppercase text-gray-500 tracking-wide mb-3 font-semibold">Full Timeline</h4>
                {/* Kickoff row */}
                {project.artworkReceived && (
                  <div className="flex justify-between text-sm pb-2 mb-2 border-b border-gray-200">
                    <span className="text-green-400 font-medium">Project Kickoff</span>
                    <span className="text-green-400 font-medium">
                      {formatDateStr(project.artworkReceived)}
                    </span>
                  </div>
                )}
                {/* Header row */}
                <div className="grid grid-cols-3 gap-2 text-[10px] uppercase text-gray-500 mb-2 pb-1 border-b border-gray-200">
                  <span>Milestone</span>
                  <span className="text-center">Due</span>
                  <span className="text-center">Actual</span>
                </div>
                {/* Timeline rows */}
                <div className="space-y-1.5 text-sm">
                  {[
                    { label: "Artwork", due: project.artworkDue, actual: project.artworkReceived },
                    { label: "Proof", due: project.proofDue, actual: project.proofSubmitted },
                    { label: "Approval", due: null, actual: project.proofApproved },
                    { label: "Finals", due: project.finalPrintsDue, actual: project.finalPrintsComplete },
                    { label: "Assembly", due: project.assemblyDue, actual: project.assemblyComplete },
                    { label: "Ship", due: project.shipDate, actual: project.actualShipDate },
                  ].map((item) => (
                    <div key={item.label} className="grid grid-cols-3 gap-2 items-center">
                      <span className="text-gray-500 text-xs">{item.label}</span>
                      <span className={`text-center ${item.due ? formatDate(item.due).className : 'text-gray-600'}`}>
                        {formatDateStr(item.due)}
                      </span>
                      <span className={`text-center ${item.actual ? 'text-green-400' : 'text-gray-600'}`}>
                        {formatDateStr(item.actual)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h4 className="text-xs uppercase text-gray-500 tracking-wide mb-3 font-semibold">Quick Actions</h4>
                <div className="space-y-2">
                  {/* Track Time Button */}
                  {orderCount > 0 && onNavigateToOrder && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const firstOrder = project.jobBossData?.orders?.[0];
                        onNavigateToOrder(project.quoteNumber, firstOrder?.orderNumber);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600/30 hover:bg-green-600/50 border border-green-500/50 rounded-lg text-green-600 hover:text-green-100 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-bold">Track Time</span>
                    </button>
                  )}
                  {/* Work Order Link */}
                  {project.workOrderUrl && (
                    <a
                      href={project.workOrderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-cyan-600/30 hover:bg-cyan-600/50 border border-cyan-500/50 rounded-lg text-cyan-600 hover:text-cyan-100 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <span className="font-bold">Work Order</span>
                    </a>
                  )}
                  {/* JobBOSS Orders */}
                  {project.jobBossData?.orders?.map((order, idx) => (
                    <a
                      key={idx}
                      href={order.jobBossUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/50 rounded-lg text-blue-600 hover:text-blue-100 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="font-bold">JobBOSS {order.orderNumber}</span>
                    </a>
                  ))}
                  {!orderCount && !project.workOrderUrl && (
                    <div className="text-gray-600 text-sm text-center py-2">No actions available</div>
                  )}
                </div>
              </div>

              {/* Components & Hours */}
              <div>
                <h4 className="text-xs uppercase text-gray-500 tracking-wide mb-3 font-semibold">Components & Hours</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {project.shrinks > 0 && <div><span className="text-blue-400 font-bold">{project.shrinks}</span> <span className="text-gray-500">Shrinks</span></div>}
                  {project.cartons > 0 && <div><span className="text-green-400 font-bold">{project.cartons}</span> <span className="text-gray-500">Cartons</span></div>}
                  {project.labels > 0 && <div><span className="text-yellow-400 font-bold">{project.labels}</span> <span className="text-gray-500">Labels</span></div>}
                  {project.bags > 0 && <div><span className="text-purple-600 font-bold">{project.bags}</span> <span className="text-gray-500">Bags</span></div>}
                  {project.other > 0 && <div><span className="text-gray-500 font-bold">{project.other}</span> <span className="text-gray-500">Other</span></div>}
                  {project.totalComps > 0 && <div className="col-span-2 pt-2 border-t border-gray-200"><span className="text-gray-900 font-bold">{project.totalComps}</span> <span className="text-gray-500">Total Comps</span></div>}
                </div>
                {(project.paHours || project.finishingHours || project.assemblyHours) && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="flex gap-4 text-sm">
                      {project.paHours && <div><span className="text-blue-400 font-bold">{project.paHours}h</span> <span className="text-gray-500">PA</span></div>}
                      {project.finishingHours && <div><span className="text-teal-400 font-bold">{project.finishingHours}h</span> <span className="text-gray-500">Finish</span></div>}
                      {project.assemblyHours && <div><span className="text-purple-600 font-bold">{project.assemblyHours}h</span> <span className="text-gray-500">Assy</span></div>}
                    </div>
                  </div>
                )}
              </div>

              {/* Notes & Team */}
              <div>
                <h4 className="text-xs uppercase text-gray-500 tracking-wide mb-3 font-semibold">Team & Notes</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Project Mgr</span>
                    <span className="text-gray-900">{project.projectManager || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Prod Artist</span>
                    <span className="text-gray-900">{project.productionArtist || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sales</span>
                    <span className="text-gray-900">{project.sales || "-"}</span>
                  </div>
                </div>
                {project.notes && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-500 uppercase mb-1">Notes</div>
                    <div className="text-sm text-gray-700 bg-slate-100 rounded p-2">{project.notes}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING / ERROR STATES
  // ═══════════════════════════════════════════════════════════════════════════
  if (loading && !scheduleData) {
    return (
      <div className="min-h-screen bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-2xl text-gray-500">Loading production schedule...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-2xl text-red-400">{error}</div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER - SIMPLIFIED
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex-1 bg-slate-200 text-gray-900 overflow-auto">
      <div className="max-w-full mx-auto px-6 py-4">
        {/* ══════════════ SIMPLE HEADER: Toggle + Search ══════════════ */}
        <div className="flex items-center gap-4 mb-4">
          {/* Active / All Toggle */}
          <div className="flex bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
            <button
              onClick={() => setActiveView("inprogress")}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeView === "inprogress"
                  ? "bg-purple-600 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Active ({metrics.inProgressCount})
            </button>
            <button
              onClick={() => setActiveView("all")}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeView === "all"
                  ? "bg-purple-600 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              All ({metrics.total})
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search projects..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm"
            />
          </div>

          {/* Result count */}
          <div className="text-sm text-gray-500">
            {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* ══════════════ PROJECT CARDS ══════════════ */}
        <div className="space-y-2">
          {/* Column Headers */}
          {filteredProjects.length > 0 && (
            <div className="sticky top-0 z-20 grid grid-cols-12 gap-4 items-center px-5 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-300 bg-slate-200 shadow-sm">
              <div className="col-span-1 text-center">PM / PA</div>
              <div className="col-span-3">Quote & Project</div>
              <div className="col-span-1 text-center">Comps</div>
              <div className="col-span-1 text-center">POA</div>
              <div className="col-span-1 text-center">Stage</div>
              <div className="col-span-3 text-center grid grid-cols-3">
                <span>Proof</span>
                <span>Finals</span>
                <span>Assembly</span>
              </div>
              <div className="col-span-1 text-center">Ship</div>
            </div>
          )}

          {filteredProjects.length === 0 ? (
            <div className="text-center py-20 text-xl text-gray-500">
              {filterText ? "No projects match your search" : "No projects in this view"}
            </div>
          ) : (
            filteredProjects.map((project, idx) => (
              <ProjectCard key={`${project.projId}-${idx}`} project={project} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
